import { logger } from "../../utils/logger";
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { format } from 'date-fns';
import { AppPageHeader } from '../../components/app/AppPage';
import {
  Alert,
  Box,
  Typography,
  Paper,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { fetchReportSummary } from '../../services/reportService';

const currency = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' });

const startOfYear = () => {
  const now = new Date();
  return new Date(now.getFullYear(), 0, 1);
};

const Reports = () => {
  const [startDate, setStartDate] = useState(startOfYear());
  const [endDate, setEndDate] = useState(new Date());
  const [reportType, setReportType] = useState('sales_tax');
  const [reportData, setReportData] = useState([]);
  const [totals, setTotals] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [hasLoaded, setHasLoaded] = useState(false);

  const range = useMemo(() => {
    if (!startDate || !endDate) {
      return null;
    }
    return {
      start: format(startDate, 'yyyy-MM-dd'),
      end: format(endDate, 'yyyy-MM-dd'),
    };
  }, [startDate, endDate]);

  const loadReport = useCallback(async () => {
    if (!range) {
      setError('Choose a start date and an end date.');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const summary = await fetchReportSummary(range.start, range.end, reportType);
      setReportData(summary.rows);
      setTotals(summary.totals);
      setHasLoaded(true);
    } catch (err) {
      logger.error('Report load failed', err);
      setReportData([]);
      setTotals(null);
      setError(err?.message || 'The report could not be loaded.');
    } finally {
      setLoading(false);
    }
  }, [range, reportType, setError]);

  useEffect(() => {
    loadReport();
    // Re-run only when the report type changes; dates are applied on demand.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reportType]);

  const handleGenerateReport = () => {
    loadReport();
  };

  const dueDateLabel = (row) => {
    if (!row.due_date) {
      return '—';
    }
    const parsed = new Date(`${row.due_date}T00:00:00`);
    return Number.isNaN(parsed.getTime()) ? row.due_date : format(parsed, 'MMM d, yyyy');
  };

  return (
    <Box className="gt-page">
      <AppPageHeader
        title="Tax reports"
        description="Choose a report type and date range."
      />
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={3}>
            <FormControl fullWidth>
              <InputLabel>Report Type</InputLabel>
              <Select
                value={reportType}
                label="Report Type"
                onChange={(e) => setReportType(e.target.value)}
              >
                <MenuItem value="sales_tax">Sales Tax Report</MenuItem>
                <MenuItem value="nexus">Nexus Report</MenuItem>
                <MenuItem value="compliance">Compliance Report</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={3}>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker
                label="Start Date"
                value={startDate}
                onChange={setStartDate}
                renderInput={(params) => <TextField {...params} fullWidth />}
              />
            </LocalizationProvider>
          </Grid>
          <Grid item xs={12} md={3}>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker
                label="End Date"
                value={endDate}
                onChange={setEndDate}
                renderInput={(params) => <TextField {...params} fullWidth />}
              />
            </LocalizationProvider>
          </Grid>
          <Grid item xs={12} md={3}>
            <Button
              variant="contained"
              fullWidth
              onClick={handleGenerateReport}
              disabled={loading}
              startIcon={loading ? <CircularProgress size={16} color="inherit" /> : undefined}
            >
              {loading ? 'Generating…' : 'Generate Report'}
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {totals && !error && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {totals.jurisdiction_count} jurisdiction{totals.jurisdiction_count === 1 ? '' : 's'} ·{' '}
          {currency.format(totals.tax_amount)} tax on {currency.format(totals.taxable_amount)} taxable
          sales · {totals.transaction_count} transaction{totals.transaction_count === 1 ? '' : 's'}
        </Typography>
      )}

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Jurisdiction</TableCell>
              <TableCell align="right">Taxable Amount</TableCell>
              <TableCell align="right">Tax Amount</TableCell>
              <TableCell>Filing Status</TableCell>
              <TableCell>Due Date</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {reportData.map((row) => (
              <TableRow key={row.jurisdiction} hover>
                <TableCell>
                  {row.jurisdiction_name}
                  {row.jurisdiction !== 'UNKNOWN' && (
                    <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                      {row.jurisdiction}
                    </Typography>
                  )}
                </TableCell>
                <TableCell align="right">{currency.format(row.taxable_amount)}</TableCell>
                <TableCell align="right">{currency.format(row.tax_amount)}</TableCell>
                <TableCell>{row.filing_status}</TableCell>
                <TableCell>{dueDateLabel(row)}</TableCell>
              </TableRow>
            ))}
            {!loading && reportData.length === 0 && (
              <TableRow>
                <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                  <Typography variant="body2" color="text.secondary">
                    {hasLoaded
                      ? 'No sales or filings in this date range. Try a wider period.'
                      : 'Run a report to see your tax by jurisdiction.'}
                  </Typography>
                </TableCell>
              </TableRow>
            )}
            {loading && reportData.length === 0 && (
              <TableRow>
                <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                  <CircularProgress size={24} />
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default Reports;
